import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Loader2, Search as SearchIcon, Scale } from "lucide-react";
import {
    WorkspaceTabs,
    DecisionTree,
    PenaltyCalculator,
    WorkspaceHero,
} from "../components/workspace/Primitives";

const TABS = [
    { key: "articles", label: "Article navigator" },
    { key: "lawful_basis", label: "Lawful-basis selector" },
    { key: "dpia", label: "DPIA assistant" },
    { key: "rights", label: "Rights explorer" },
    { key: "breach", label: "Breach decision tree" },
    { key: "transfers", label: "Transfer wizard" },
    { key: "penalties", label: "Penalties" },
];

export default function GdprWorkspace() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("articles");

    useEffect(() => {
        api.get("/gdpr").then(({ data }) => setData(data));
    }, []);

    if (!data) {
        return (
            <div className="container-edit py-14 flex items-center gap-3 text-[#0D1B3E]/60">
                <Loader2 className="animate-spin" size={18} />
                <span className="label-eyebrow">Loading GDPR workspace…</span>
            </div>
        );
    }

    return (
        <div className="container-edit py-10 lg:py-14" data-testid="gdpr-workspace">
            <WorkspaceHero
                eyebrow="Workspace · Regulation (EU) 2016/679"
                title="GDPR"
                subtitle="Navigate articles, pick a lawful basis, screen for DPIA triggers, walk through data-subject rights, classify breaches, route cross-border transfers, and model worst-case fines."
                badges={["In force since 25 May 2018", "Chapter V transfers updated post-Schrems II"]}
            />

            <div className="mt-8">
                <WorkspaceTabs tabs={TABS} active={tab} onChange={setTab} testIdPrefix="gdpr" />
            </div>

            <div className="mt-9">
                {tab === "articles" && <ArticleNavigator articles={data.articles} />}
                {tab === "lawful_basis" && <LawfulBasis selector={data.lawful_basis} />}
                {tab === "dpia" && <DpiaAssistant data={data.dpia} />}
                {tab === "rights" && <RightsExplorer rights={data.rights} />}
                {tab === "breach" && (
                    <section className="max-w-3xl" data-testid="gdpr-breach">
                        <DecisionTree tree={data.breach} testIdPrefix="gdpr-breach" renderOutcome={renderBreachOutcome} />
                    </section>
                )}
                {tab === "transfers" && (
                    <section className="max-w-3xl" data-testid="gdpr-transfers">
                        <DecisionTree tree={data.transfers} testIdPrefix="gdpr-transfers" renderOutcome={renderTransferOutcome} />
                    </section>
                )}
                {tab === "penalties" && (
                    <section data-testid="gdpr-penalties">
                        <PenaltyCalculator bands={data.penalties} testIdPrefix="gdpr" />
                        <p className="mt-5 text-xs text-[#0D1B3E]/60">
                            Article 83 ceilings. Member-State law may impose criminal sanctions in addition.
                        </p>
                    </section>
                )}
            </div>
        </div>
    );
}

function ArticleNavigator({ articles }) {
    const [q, setQ] = useState("");
    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return articles;
        return articles.filter(
            (a) =>
                a.number.toLowerCase().includes(s) ||
                a.title.toLowerCase().includes(s) ||
                a.chapter.toLowerCase().includes(s),
        );
    }, [q, articles]);

    const grouped = useMemo(() => {
        const map = {};
        for (const a of filtered) {
            if (!map[a.chapter]) map[a.chapter] = [];
            map[a.chapter].push(a);
        }
        return map;
    }, [filtered]);

    return (
        <section data-testid="gdpr-articles">
            <div className="card-flat p-5">
                <div className="relative">
                    <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1B3E]/50" />
                    <input
                        className="field pl-9"
                        placeholder="Search by number or title, e.g. 'Art. 33' or 'erasure'"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="gdpr-articles-search"
                    />
                </div>
            </div>
            <div className="mt-6 space-y-7">
                {Object.entries(grouped).map(([chapter, items]) => (
                    <div key={chapter}>
                        <div className="label-eyebrow">{chapter}</div>
                        <ul className="mt-3 grid sm:grid-cols-2 gap-2.5">
                            {items.map((a) => (
                                <li
                                    key={a.number}
                                    className="card-flat p-4 flex items-start gap-3"
                                    data-testid={`gdpr-article-${a.number}`}
                                >
                                    <div className="font-display text-lg tracking-tight text-[#C9A84C] shrink-0">
                                        Art. {a.number}
                                    </div>
                                    <div className="text-sm text-[#0D1B3E]/85 leading-relaxed">
                                        {a.title}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                {filtered.length === 0 && (
                    <div className="text-sm text-[#0D1B3E]/60" data-testid="gdpr-articles-empty">
                        No articles match.
                    </div>
                )}
            </div>
        </section>
    );
}

function LawfulBasis({ selector }) {
    return (
        <section className="grid lg:grid-cols-[1.4fr_1fr] gap-9" data-testid="gdpr-lawful-basis">
            <DecisionTree
                tree={{
                    questions: selector.questions,
                    outcomes: { ...selector.bases, none: selector.outcomes_none },
                }}
                testIdPrefix="gdpr-basis"
                renderOutcome={(key, outcome) => (
                    <div
                        className="card-flat border-2 border-[#C9A84C] p-7 bg-[#FCF7E9]"
                        data-testid={`gdpr-basis-outcome-${key}`}
                    >
                        <div className="label-eyebrow">{outcome.article}</div>
                        <h3 className="mt-2 font-display text-2xl tracking-tight">{outcome.label}</h3>
                        <p className="mt-3 text-sm text-[#0D1B3E]/85 leading-relaxed">
                            {outcome.summary}
                        </p>
                        {outcome.watch_outs?.length > 0 && (
                            <div className="mt-4">
                                <div className="label-eyebrow">Watch-outs</div>
                                <ul className="mt-2 space-y-1.5 text-sm text-[#0D1B3E]/80">
                                    {outcome.watch_outs.map((w, i) => (
                                        <li key={i} className="flex gap-2">
                                            <span>–</span>
                                            <span>{w}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            />
            <aside>
                <div className="label-eyebrow">All six bases</div>
                <ul className="mt-3 space-y-2">
                    {Object.entries(selector.bases).map(([k, b]) => (
                        <li key={k} className="card-flat p-4">
                            <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                {b.article}
                            </div>
                            <div className="mt-1 font-display text-base tracking-tight">
                                {b.label}
                            </div>
                            <p className="mt-1 text-xs text-[#0D1B3E]/70 leading-relaxed">
                                {b.summary}
                            </p>
                        </li>
                    ))}
                </ul>
            </aside>
        </section>
    );
}

function DpiaAssistant({ data }) {
    const [checked, setChecked] = useState({});
    const count = Object.values(checked).filter(Boolean).length;
    const out = count >= data.decision.threshold
        ? data.decision.outcomes.required
        : count >= 1
        ? data.decision.outcomes.advised
        : data.decision.outcomes.not_required;
    const toneCls =
        out.tone === "warning"
            ? "bg-amber-50 border-amber-300"
            : out.tone === "info"
            ? "bg-sky-50 border-sky-300"
            : "bg-emerald-50 border-emerald-300";

    return (
        <section className="max-w-3xl" data-testid="gdpr-dpia">
            <p className="text-sm text-[#0D1B3E]/75">
                Check every trigger that applies to the processing activity. We&apos;ll tell you whether a DPIA is required under Article 35.
            </p>
            <ul className="mt-5 space-y-2.5">
                {data.triggers.map((t) => {
                    const ok = !!checked[t.id];
                    return (
                        <li key={t.id} className="card-flat p-4 flex items-start gap-3">
                            <input
                                type="checkbox"
                                checked={ok}
                                onChange={() => setChecked((c) => ({ ...c, [t.id]: !c[t.id] }))}
                                className="mt-1"
                                data-testid={`gdpr-dpia-trigger-${t.id}`}
                            />
                            <span className="text-sm text-[#0D1B3E]/90 leading-relaxed">
                                {t.text}
                            </span>
                        </li>
                    );
                })}
            </ul>
            <div
                className={`mt-7 card-flat border-2 p-6 ${toneCls}`}
                data-testid="gdpr-dpia-outcome"
            >
                <div className="label-eyebrow">
                    {count} trigger{count === 1 ? "" : "s"} selected
                </div>
                <h3 className="mt-2 font-display text-2xl tracking-tight">{out.label}</h3>
                <p className="mt-2 text-sm text-[#0D1B3E]/85 leading-relaxed">{out.body}</p>
            </div>
        </section>
    );
}

function RightsExplorer({ rights }) {
    const [open, setOpen] = useState(rights[0]?.article);
    const active = rights.find((r) => r.article === open) || rights[0];
    return (
        <section className="grid lg:grid-cols-[1fr_1.6fr] gap-7" data-testid="gdpr-rights">
            <aside>
                <div className="label-eyebrow">Data-subject rights</div>
                <ul className="mt-3 space-y-1.5">
                    {rights.map((r) => (
                        <li key={r.article}>
                            <button
                                type="button"
                                onClick={() => setOpen(r.article)}
                                className={`w-full text-left card-flat p-3 transition-colors ${
                                    active.article === r.article
                                        ? "border-[#C9A84C]"
                                        : "hover:border-[#C9A84C]/60"
                                }`}
                                data-testid={`gdpr-right-${r.article.replace(/\s+/g, "-").replace(/\./g, "")}`}
                            >
                                <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                    {r.article}
                                </div>
                                <div className="mt-0.5 font-display text-base">{r.right}</div>
                            </button>
                        </li>
                    ))}
                </ul>
            </aside>
            <div className="card-flat p-7" data-testid="gdpr-right-detail">
                <Scale size={16} color="#C9A84C" />
                <div className="mt-3 label-eyebrow">{active.article}</div>
                <h3 className="mt-1 font-display text-2xl tracking-tight">{active.right}</h3>
                <div className="mt-3 inline-block text-xs uppercase tracking-[0.18em] px-2 py-1 border border-[#0D1B3E]/20">
                    Deadline · {active.deadline}
                </div>
                <div className="mt-5">
                    <div className="label-eyebrow">Response playbook</div>
                    <ol className="mt-3 space-y-2 text-sm text-[#0D1B3E]/85 leading-relaxed list-decimal pl-5">
                        {active.playbook.map((p, i) => (
                            <li key={i}>{p}</li>
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    );
}

function renderBreachOutcome(key, outcome) {
    const toneCls =
        key === "notify_both"
            ? "bg-rose-50 border-rose-300"
            : key === "notify_sa"
            ? "bg-amber-50 border-amber-300"
            : "bg-emerald-50 border-emerald-300";
    return (
        <div
            className={`card-flat border-2 p-7 ${toneCls}`}
            data-testid={`gdpr-breach-outcome-${key}`}
        >
            <div className="label-eyebrow">Outcome</div>
            <h3 className="mt-2 font-display text-2xl tracking-tight">{outcome.label}</h3>
            <p className="mt-3 text-sm text-[#0D1B3E]/85 leading-relaxed">{outcome.body}</p>
            {outcome.deadlines?.length > 0 && (
                <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                    {outcome.deadlines.map((d, i) => (
                        <li key={i} className="border border-[#0D1B3E]/20 p-3 bg-white">
                            <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                {d.label}
                            </div>
                            <div className="mt-1 text-sm font-semibold">{d.value}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function renderTransferOutcome(key, outcome) {
    return (
        <div
            className="card-flat border-2 border-[#C9A84C] p-7 bg-[#FCF7E9]"
            data-testid={`gdpr-transfer-outcome-${key}`}
        >
            <div className="label-eyebrow">{outcome.art}</div>
            <h3 className="mt-2 font-display text-2xl tracking-tight">{outcome.label}</h3>
            <p className="mt-3 text-sm text-[#0D1B3E]/85 leading-relaxed">{outcome.body}</p>
        </div>
    );
}
